import { computed, ref, shallowRef, type Ref } from 'vue'

import axios, { AxiosError } from 'axios'
import { defineStore, storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'

import { handleApiErrorToast } from '@/apiClient'
import { UploadApi } from '@/apiClient/modules/upload'
import { adminQueryKeys } from '@/apiClient/queries/useAdminQueries'
import type { IUploadedFile } from '@/apiClient/types/file'
import type { SharePointUnsyncTarget } from '@/apiClient/types/sharePointRag'
import { queryClient } from '@/lib/queryClient'
import { isUploadedFileStatusInProgress } from '@/utils/fileUploadStatus'
import { validateFileSignature } from '@/utils/fileValidation'
import type { IImageDimensions } from '@/utils/imageDimensions'
import { readImageDimensions } from '@/utils/imageDimensions'
import {
  DUPLICATE_IN_UPLOAD_LIST_MESSAGE,
  prepareFilesForUpload,
} from '@/utils/uploadFileDedup'

import { useAlert } from '../alert'
import { useModelStore } from '../model'
import { useToast } from '../toast'

export const useFileStore = defineStore('file', () => {
  const route = useRoute()
  const { onSuccess, onError, onWarning } = useToast()
  const { activeModel, selectableModels, doesModelSupportFileUpload } =
    storeToRefs(useModelStore())

  const isDragging = ref(false)
  const isUploading = ref(false)

  /**
   * Array of all files (both local and uploaded). A mixed array of File and IUploadedFile instances.
   * This is needed to be able to show a mixed list of files in the UI that are uploaded and still uploading.
   * i.e. In the ChatInput component.
   */
  const fileList = ref<(File | IUploadedFile)[]>([])
  /**
   * Files that have been uploaded and returned from the API.
   * @returns IUploadedFile[]
   */
  const uploadedFiles = computed(
    () =>
      fileList.value.filter(
        (file) => 'id' in file,
      ) as unknown as IUploadedFile[],
  )
  /** Whether any uploaded file is still being processed on the backend. */
  const hasUploadedFilesProcessing = computed(() =>
    uploadedFiles.value.some((file) =>
      isUploadedFileStatusInProgress(file.status),
    ),
  )
  /**
   * Files that have not been uploaded yet.
   * @returns File[]
   */
  const localFiles = computed(
    () =>
      fileList.value.filter((file) => !(file as IUploadedFile).id) as File[],
  )

  /**
   * GPT upsert form only — mixed local `File` and uploaded `IUploadedFile` rows.
   * Separate from chat `fileList` so uploads and websocket processing updates stay scoped.
   */
  const gptFileList = ref<(File | IUploadedFile)[]>([])
  const gptIsUploading = ref(false)
  const gptUploadingFiles = shallowRef(new Set<File>())
  /**
   * SharePoint files removed from the GPT during this edit session, awaiting an
   * `/unsync` cleanup once the GPT is saved. Keyed by SharePoint item id (stable
   * across remove/re-add); the value carries the DB File-row id and coordinates
   * that `/unsync` receives. Re-adding a removed file before saving cancels its
   * pending unsync. Drained by {@link takeSharePointFilesPendingUnsync}.
   */
  const sharePointFilesPendingUnsync = ref(
    new Map<string, SharePointUnsyncTarget>(),
  )
  const gptUploadedFiles = computed(
    () =>
      gptFileList.value.filter(
        (file) => 'id' in file,
      ) as unknown as IUploadedFile[],
  )
  /**
   * Whether any GPT-upsert file is still uploading or being processed (RAG ingest).
   * Tracks the local file list directly, so it reflects freshly added files
   * before the GPT is saved — unlike `gpt.status`, which only updates after Update.
   */
  const gptHasFilesProcessing = computed(
    () =>
      gptIsUploading.value ||
      gptUploadedFiles.value.some((file) =>
        isUploadedFileStatusInProgress(file.status),
      ),
  )

  /** Unique id per File reference, so we can key abortControllers reliably. */
  const fileIdByRef = new WeakMap<File, string>()
  const getFileId = (file: File): string => {
    let id = fileIdByRef.get(file)
    if (!id) {
      id = crypto.randomUUID()
      fileIdByRef.set(file, id)
    }
    return id
  }

  const abortControllers = ref<Map<string, AbortController>>(new Map())
  /**
   * Intrinsic pixel size per uploaded image id, measured from the local `File` in
   * the composer — the only point at which the real shape is known, since nothing
   * persists pixel dimensions. Lets the loading skeleton reserve the right box
   * instead of a square that snaps when the image decodes.
   *
   * Deliberately outlives `clearUploadedFiles()`: the composer is emptied on send,
   * but the message that just went out still has to render those images.
   */
  const imageNaturalSizeById = ref<Record<string, IImageDimensions>>({})

  const gptFileIdByRef = new WeakMap<File, string>()
  const getGptFileId = (file: File): string => {
    let id = gptFileIdByRef.get(file)
    if (!id) {
      id = crypto.randomUUID()
      gptFileIdByRef.set(file, id)
    }
    return id
  }

  const gptAbortControllers = ref<Map<string, AbortController>>(new Map())

  const setGptFileUploading = (file: File, uploading: boolean) => {
    const next = new Set(gptUploadingFiles.value)
    if (uploading) {
      next.add(file)
    } else {
      next.delete(file)
    }
    gptUploadingFiles.value = next
  }

  const isGptLocalFileUploading = (file: File | IUploadedFile) =>
    file instanceof File && gptUploadingFiles.value.has(file)

  const gptUploadSingleFile = async (file: File, useRag?: boolean) => {
    const isValid = await validateFileSignature(file)
    if (!isValid) {
      onError(
        'File content does not match its extension. The file may be corrupted or malicious.',
      )
      gptFileList.value = gptFileList.value.filter((f) => f !== file)
      return
    }

    const controller = new AbortController()
    const fileId = getGptFileId(file)
    gptAbortControllers.value.set(fileId, controller)
    setGptFileUploading(file, true)

    return UploadApi.file(file, { signal: controller.signal }, useRag)
      .then(([uploadedFile]) => {
        gptFileList.value = gptFileList.value.map((f) =>
          f === file ? uploadedFile : f,
        )
      })
      .catch((error: unknown) => {
        gptFileList.value = gptFileList.value.filter((f) => f !== file)
        if (
          axios.isCancel(error) ||
          (error instanceof AxiosError && error.code === 'ERR_CANCELED')
        ) {
          return
        }
        return handleApiErrorToast(error as AxiosError<unknown>)
      })
      .finally(() => {
        gptAbortControllers.value.delete(fileId)
        setGptFileUploading(file, false)
      })
  }

  /**
   * GPT upsert: enqueue files and upload each (optional RAG flag for ingest).
   */
  const gptHandleFileSelect = (files: File[], useRag?: boolean) => {
    const { accepted, duplicateCount } = prepareFilesForUpload(
      files,
      gptFileList.value,
    )

    if (duplicateCount > 0) {
      onWarning(DUPLICATE_IN_UPLOAD_LIST_MESSAGE)
    }

    if (!accepted.length) return

    accepted.forEach((file) => gptFileList.value.push(file))

    gptIsUploading.value = true
    return Promise.all(
      accepted.map((localFile) =>
        gptUploadSingleFile(localFile, useRag).catch(() => {
          gptFileList.value = gptFileList.value.filter((f) => f !== localFile)
        }),
      ),
    ).finally(() => {
      gptIsUploading.value = false
    })
  }

  /**
   * The unsync target (DB File-row id + SharePoint coordinates) for a GPT file, or
   * null if it isn't a registered SharePoint file. `item_id` keys the pending-unsync
   * map (stable across remove/re-add); the whole target is what `/unsync` receives.
   */
  const sharePointUnsyncTargetOf = (
    file: File | IUploadedFile,
  ): SharePointUnsyncTarget | null => {
    if (!('id' in file)) return null
    const sharepoint = file.status_details?.sharepoint
    if (!sharepoint?.site_id || !sharepoint.drive_id || !sharepoint.item_id) {
      return null
    }
    return {
      id: file.id,
      site_id: sharepoint.site_id,
      drive_id: sharepoint.drive_id,
      item_id: sharepoint.item_id,
    }
  }

  const gptRemoveUploadedFile = (fileToRemove: File | IUploadedFile) => {
    // Queue SharePoint files for `/unsync` cleanup after the next GPT save.
    const unsyncTarget = sharePointUnsyncTargetOf(fileToRemove)
    if (unsyncTarget) {
      sharePointFilesPendingUnsync.value.set(unsyncTarget.item_id, unsyncTarget)
    }

    if ('id' in fileToRemove) {
      gptFileList.value = gptFileList.value.filter(
        (file) => !('id' in file) || file.id !== fileToRemove.id,
      )
      return
    }

    gptFileList.value = gptFileList.value.filter(
      (file) => file !== fileToRemove,
    )
  }

  const gptCancelUpload = (file: File | IUploadedFile) => {
    if ('id' in file) {
      gptRemoveUploadedFile(file)
      return
    }

    gptFileList.value = gptFileList.value.filter((f) => f !== file)

    const fileId = getGptFileId(file)
    const controller = gptAbortControllers.value.get(fileId)
    if (controller) {
      controller.abort()
      gptAbortControllers.value.delete(fileId)
      setGptFileUploading(file, false)
    }
  }

  const syncGptFileListFromConfig = (files: IUploadedFile[]) => {
    gptFileList.value = [...files]
  }

  /**
   * Append already-uploaded files (e.g. SharePoint files registered via `/sync`)
   * to the GPT file list, so they become part of `config.files` on save. Deduped
   * by id; their processing status then updates in place via {@link updateUploadedFile}.
   */
  const addGptUploadedFiles = (files: IUploadedFile[]) => {
    // Re-adding a file that was removed this session cancels its pending unsync.
    for (const file of files) {
      const itemId = file.status_details?.sharepoint?.item_id
      if (itemId) sharePointFilesPendingUnsync.value.delete(itemId)
    }

    const existingIds = new Set(
      gptFileList.value
        .filter((file) => 'id' in file)
        .map((file) => (file as IUploadedFile).id),
    )
    const fresh = files.filter((file) => !existingIds.has(file.id))
    if (fresh.length) gptFileList.value = [...gptFileList.value, ...fresh]
  }

  /**
   * Drain the SharePoint files removed since the last drain (or store clear) and
   * return their unsync targets, so the caller can `/unsync` them. Call after a
   * successful GPT save — the save has already removed them from `config.files`.
   */
  const takeSharePointFilesPendingUnsync = (): SharePointUnsyncTarget[] => {
    const targets = [...sharePointFilesPendingUnsync.value.values()]
    sharePointFilesPendingUnsync.value = new Map()
    return targets
  }

  /**
   * The queued unsync targets for the given SharePoint item ids (peek — the queue
   * is left intact; {@link addGptUploadedFiles} clears them once the re-sync
   * lands). Lets a re-add flush the removal the server hasn't been told about yet:
   * a removed file's File row stays attached to the GPT until a save or `/unsync`,
   * and re-syncing it while attached is rejected as a duplicate.
   */
  const sharePointUnsyncTargetsFor = (
    itemIds: string[],
  ): SharePointUnsyncTarget[] =>
    itemIds
      .map((itemId) => sharePointFilesPendingUnsync.value.get(itemId))
      .filter(
        (target): target is SharePointUnsyncTarget => target !== undefined,
      )

  const clearGptFileList = () => {
    for (const controller of gptAbortControllers.value.values()) {
      controller.abort()
    }
    gptAbortControllers.value.clear()
    gptUploadingFiles.value = new Set()
    gptIsUploading.value = false
    gptFileList.value = []
    sharePointFilesPendingUnsync.value = new Map()
  }

  /**
   * Uploads a single file
   * @param file File
   * @returns Promise<IUploadedFile>
   */
  const uploadSingleFile = async (file: File) => {
    // Validate file signature
    const isValid = await validateFileSignature(file)
    if (!isValid) {
      onError(
        'File content does not match its extension. The file may be corrupted or malicious.',
      )
      // Remove the file from the local files list
      fileList.value = fileList.value.filter((f) => f !== file)
      return
    }

    const controller = new AbortController()
    const fileId = getFileId(file)
    abortControllers.value.set(fileId, controller)

    const naturalSizePromise = readImageDimensions(file)

    return UploadApi.file(file, { signal: controller.signal })
      .then(([uploadedFile]) => {
        // Find and replace the local file with the uploaded file (File -> IUploadedFile)
        fileList.value = fileList.value.map((f) =>
          f === file ? uploadedFile : f,
        )
        // Deliberately detached: awaiting the decode here would hold `isUploading`
        // true past the upload and keep the send button disabled. The size only
        // shapes a loading box, so it is fine for it to land a frame late.
        void naturalSizePromise.then((naturalSize) => {
          if (naturalSize) {
            imageNaturalSizeById.value[uploadedFile.id] = naturalSize
          }
        })
      })
      .catch((error: unknown) => {
        fileList.value = fileList.value.filter((f) => f !== file)
        if (
          axios.isCancel(error) ||
          (error instanceof AxiosError && error.code === 'ERR_CANCELED')
        ) {
          return
        }
        return handleApiErrorToast(error as AxiosError<unknown>)
      })
      .finally(() => {
        abortControllers.value.delete(fileId)
      })
  }

  /**
   * Upload an array of files. Calls UploadApi.file() for each file.
   * @param files File[]
   */
  const uploadFiles = (files: File[]) => {
    const { accepted, duplicateCount } = prepareFilesForUpload(
      files,
      fileList.value,
    )

    if (duplicateCount > 0) {
      onWarning(DUPLICATE_IN_UPLOAD_LIST_MESSAGE)
    }

    if (!accepted.length) return

    accepted.forEach((file) => fileList.value.push(file))

    isUploading.value = true
    return Promise.all(localFiles.value.map(uploadSingleFile)).finally(() => {
      isUploading.value = false
    })
  }

  /**
   * Removes a file from the file list (supports both local `File` and uploaded `IUploadedFile`).
   */
  const removeUploadedFile = (fileToRemove: File | IUploadedFile) => {
    if ('id' in fileToRemove) {
      fileList.value = fileList.value.filter(
        (file) => !('id' in file) || file.id !== fileToRemove.id,
      )
      return
    }

    fileList.value = fileList.value.filter((file) => file !== fileToRemove)
  }

  /**
   * Cancels an in-progress upload (or removes an already-uploaded file).
   */
  const cancelUpload = (file: File | IUploadedFile) => {
    if ('id' in file) {
      removeUploadedFile(file)
      return
    }

    fileList.value = fileList.value.filter((f) => f !== file)
    const fileId = getFileId(file)
    const controller = abortControllers.value.get(fileId)
    if (controller) {
      controller.abort()
      abortControllers.value.delete(fileId)
    }
  }

  /**
   * Handles a drag and drop event
   * @param event DragEvent
   */
  const handleDragUpload = (event: DragEvent) => {
    if (!doesModelSupportFileUpload.value) {
      useAlert().onWarning(
        'File upload not supported',
        `The selected model <${activeModel.value?.display_name}> doesn't support file uploads. Please switch to a different model to enable this feature.`,
        {
          actions: {
            'Go back': () => useAlert().clearAlerts(),
            'Switch model': () => {
              const compatableModel = selectableModels.value.find(
                (model) => model.features.vision,
              )
              if (!compatableModel) {
                onError('No available models support file uploads.')
              } else {
                useModelStore().setActiveModel(compatableModel)
                onSuccess(
                  `Switched to the model: "${compatableModel.display_name}"`,
                )
              }

              useAlert().clearAlerts()
            },
          },
        },
      )
      isDragging.value = false

      return
    }
    event.preventDefault()
    event.stopPropagation()
    isDragging.value = false

    const files = event.dataTransfer?.files
    if (!files?.length) return

    return uploadFiles(Array.from(files))
  }

  const clearUploadedFiles = () => {
    fileList.value = []
  }

  const mergeUploadedIntoList = (
    list: Ref<(File | IUploadedFile)[]>,
    id: string,
    data: Partial<IUploadedFile>,
  ) => {
    list.value = list.value.map((file) => {
      if ('id' in file && file.id === id) {
        if (file.is_rag) {
          if (data.status === 'processed') {
            useToast().onSuccess('File processed successfully')
          } else if (data.status === 'failed') {
            useToast().onError(data.error || 'File processing failed')
          } else if (
            data.status === 'retrying' &&
            data.error &&
            file.origin === 'sharepoint'
          ) {
            // A SharePoint sync deferred server-side (RAG is still draining the
            // file's previous removal). The row stays non-terminal and the backend
            // re-drives it, so this is a warning, not an error — and the message
            // explains why the row keeps spinning. Scoped to SharePoint because
            // plain uploads emit `retrying` on every internal attempt and have
            // always been silent about it.
            useToast().onWarning(data.error)
          }
        }
        return { ...file, ...data }
      }
      return file
    })
  }

  /**
   * The GPT detail query behind the edit page the user is on, or `undefined`
   * anywhere else. The two edit pages read different endpoints, so they have
   * different cache keys (see `adminQueryKeys.gpt`) — the route decides which
   * entry carries the file's status.
   */
  const currentGptDetailQueryKey = (): readonly unknown[] | undefined => {
    const uuid = route.params.uuid as string | undefined
    if (!uuid) return undefined
    if (route.name === 'GptUpdate') return ['gpts', 'get', uuid]
    if (route.name === 'AdminGptUpdate') return adminQueryKeys.gpt(uuid)
    return undefined
  }

  const updateUploadedFile = (fileId: string, data: Partial<IUploadedFile>) => {
    mergeUploadedIntoList(fileList, fileId, data)
    mergeUploadedIntoList(gptFileList, fileId, data)

    // On a GPT edit page, invalidate its detail query to ensure the status is updated
    const queryKey = currentGptDetailQueryKey()
    if (!queryKey) return

    // Wait for status to be updated on the BE
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey })
    }, 2000)
  }

  return {
    isDragging,
    isUploading,
    hasUploadedFilesProcessing,
    fileList,
    uploadedFiles,
    localFiles,
    abortControllers,
    getFileId,
    uploadFiles,
    imageNaturalSizeById,
    handleDragUpload,
    removeUploadedFile,
    clearUploadedFiles,
    cancelUpload,
    updateUploadedFile,
    gptFileList,
    gptUploadedFiles,
    gptHasFilesProcessing,
    gptIsUploading,
    isGptLocalFileUploading,
    gptUploadSingleFile,
    gptHandleFileSelect,
    gptRemoveUploadedFile,
    gptCancelUpload,
    syncGptFileListFromConfig,
    addGptUploadedFiles,
    takeSharePointFilesPendingUnsync,
    sharePointUnsyncTargetsFor,
    clearGptFileList,
  }
})
